import * as React from "react";

/**
 * Lucide-style icon prop interface kept intentionally small.
 */
export interface IconProps extends React.SVGProps<SVGSVGElement> {
	size?: string | number;
	color?: string;
	strokeWidth?: string | number;
	absoluteStrokeWidth?: boolean;
}

/**
 * Internal base component handling shared sizing / stroke logic.
 */
const IconBase = React.forwardRef<SVGSVGElement, IconProps>(function IconBase(
	{
		size = 24,
		color = "currentColor",
		strokeWidth = 2,
		absoluteStrokeWidth = false,
		children,
		className,
		...rest
	},
	ref,
) {
	const numericSize =
		typeof size === "string" ? parseFloat(size) || 24 : size || 24;
	const resolvedSize = typeof size === "number" ? `${size}px` : size;
	let computedStrokeWidth =
		typeof strokeWidth === "string"
			? parseFloat(strokeWidth) || 2
			: strokeWidth;
	if (absoluteStrokeWidth) {
		computedStrokeWidth = (24 / numericSize) * computedStrokeWidth;
	}
	return (
		<svg
			ref={ref}
			xmlns="http://www.w3.org/2000/svg"
			width={resolvedSize}
			height={resolvedSize}
			viewBox="0 0 24 24"
			fill="none"
			stroke={color}
			strokeWidth={computedStrokeWidth}
			strokeLinecap="round"
			strokeLinejoin="round"
			role={rest["aria-label"] ? "img" : "presentation"}
			className={className}
			{...rest}
		>
			{children}
		</svg>
	);
});

function createIcon(displayName: string, paths: React.ReactNode) {
	const Comp = React.forwardRef<SVGSVGElement, IconProps>(
		function Icon(props, ref) {
			return (
				<IconBase ref={ref} {...props}>
					{paths}
				</IconBase>
			);
		},
	);
	Comp.displayName = displayName;
	return Comp;
}

// Icons
export const Play = createIcon("Play", <path d="m6 3 14 9-14 9z" />);

export const Pause = createIcon(
	"Pause",
	<>
		<rect width="4" height="16" x="14" y="4" rx="1" />
		<rect width="4" height="16" x="6" y="4" rx="1" />
	</>,
);

export const Volume1 = createIcon(
	"Volume1",
	<path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298zM16 9a5 5 0 0 1 0 6" />,
);

export const Volume2 = createIcon(
	"Volume2",
	<path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298zM16 9a5 5 0 0 1 0 6m3.364 3.364a9 9 0 0 0 0-12.728" />,
);

export const VolumeX = createIcon(
	"VolumeX",
	<path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298zM22 9l-6 6m0-6 6 6" />,
);

export const Maximize2 = createIcon(
	"Maximize2",
	<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />,
);

export const RotateCw = createIcon(
	"RotateCw",
	<>
		<path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
		<path d="M21 3v5h-5" />
	</>,
);

export const Settings = createIcon(
	"Settings",
	<>
		<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2" />
		<circle cx="12" cy="12" r="3" />
	</>,
);

export type { IconProps as IconComponentProps };

export const icons = {
	Play,
	Pause,
	Volume1,
	Volume2,
	VolumeX,
	Maximize2,
	RotateCw,
	Settings,
};

export type IconName = keyof typeof icons;

export function getIconComponent(name: IconName) {
	return icons[name];
}
